import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { businessApi, API_BASE_URL } from '../services/api';
import { API_CONFIG, testApiConnection } from '../config/apiConfig';
import { ROUTES, MESSAGES, STORAGE_KEYS, VALIDATION } from '../constants/Constants';
import { validateForm, businessValidationSchema } from '../utils/validation';
import Sidebar from './Sidebar'; // Import the reusable Sidebar component
import { Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import './AddBusiness.css';
import { FaUser, FaEnvelope, FaMapMarkerAlt, FaCity, FaFlag, FaGlobe, FaPhone, FaListAlt, FaFileImage, FaAlignLeft, FaHashtag } from 'react-icons/fa';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { indianStates, majorIndianCities } from '../constants/IndianLocations';
import { businessCategories } from '../constants/BusinessCategories';
import { useDropzone } from 'react-dropzone';


const AddBusiness = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();

    // All hooks must be called before any conditional returns
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        category: ''
    });
    const [generating, setGenerating] = useState(false);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [profilePhotos, setProfilePhotos] = useState([]);
    const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
    }, [isAuthenticated, user, navigate]);

    // Show loading while checking authentication
    if (!isAuthenticated || !user) {
        return (
            <Container fluid>
                <Row>
                    <Col md={{ span: 8, offset: 2 }} className="p-4 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Redirecting to login...</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    const countWords = (text = '') => {
        return text.trim().split(/\s+/).filter(Boolean).length;
    };

    const ensureMinWords = (text, minWords = 750, businessData = {}) => {
        const currentWords = countWords(text || '');
        if (currentWords >= minWords) return text;

        const expandedFallback = generateFallbackDescription(
            businessData.name || formData.name,
            businessData.category || formData.category,
            businessData.city || formData.city,
            820
        );

        // If we already have some AI content, keep it and append additional detailed content
        if (text && text.trim().length > 0) {
            return `${text.trim()}\n\n${expandedFallback}`;
        }

        return expandedFallback;
    };

    const handleGenerateDescription = async () => {
        if (!formData.name || !formData.category || !formData.city) {
            setError('Please enter Business Name, Category, and City to generate a description.');
            setTimeout(() => setError(null), 3000);
            return;
        }
        
        setGenerating(true);
        setError(null);
        setSuccess(null);
        
        // Always generate a description - try API first, then use fallback
        let descriptionGenerated = false;
        
        try {
            console.log('Generating description with:', {
                name: formData.name,
                category: formData.category,
                city: formData.city
            });

            // Try API first with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            try {
                // Check if we have a direct AI API key configured (for direct frontend calls)
                const aiApiKey = process.env.REACT_APP_AI_API_KEY;
                const aiApiType = process.env.REACT_APP_AI_API_TYPE || 'backend';
                
                let response;
                
                // If API key is configured and type is not 'backend', use direct AI API
                if (aiApiKey && aiApiType !== 'backend') {
                    console.log('Using direct AI API:', aiApiType);
                    response = await callDirectAIApi(aiApiKey, aiApiType, formData.name, formData.category, formData.city, controller);
                } else {
                    // Use backend endpoint (recommended)
                    response = await fetch(`${API_BASE_URL}/generate_description.php`, {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                            name: formData.name,
                            category: formData.category,
                            city: formData.city
                        }),
                        signal: controller.signal
                    });
                }

                clearTimeout(timeoutId);
                console.log('Response status:', response.status);
                console.log('Response headers:', Object.fromEntries(response.headers.entries()));

                if (response.ok) {
                    const responseText = await response.text();
                    console.log('Raw response text:', responseText);
                    console.log('Response text length:', responseText.length);
                    
                    // Try to parse as JSON
                    let data = null;
                    try {
                        data = JSON.parse(responseText);
                        console.log('Parsed JSON data:', data);
                    } catch (parseError) {
                        console.log('Not JSON, treating as plain text');
                        // If not JSON, use as plain text if it's not empty
                        if (responseText && responseText.trim().length > 10) {
                            console.log('Using plain text response as description');
                            const finalDescription = ensureMinWords(responseText.trim(), 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        } else {
                            console.warn('Response text too short or empty:', responseText);
                        }
                    }

                    // Handle JSON response in various formats
                    if (data) {
                        // Format 1: { success: true, description: "..." }
                        if (data.success && data.description) {
                            console.log('Found description in data.success format');
                            const finalDescription = ensureMinWords(data.description, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        // Format 2: { description: "..." }
                        else if (data.description) {
                            console.log('Found description in data.description format');
                            const finalDescription = ensureMinWords(data.description, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        // Format 3: { text: "..." } or { content: "..." } or { message: "..." }
                        else if (data.text) {
                            console.log('Found description in data.text format');
                            const finalDescription = ensureMinWords(data.text, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        else if (data.content) {
                            console.log('Found description in data.content format');
                            const finalDescription = ensureMinWords(data.content, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        else if (data.message && !data.error) {
                            console.log('Found description in data.message format');
                            const finalDescription = ensureMinWords(data.message, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        // Format 4: { data: { description: "..." } }
                        else if (data.data && data.data.description) {
                            console.log('Found description in data.data.description format');
                            const finalDescription = ensureMinWords(data.data.description, 750, {
                                name: formData.name,
                                category: formData.category,
                                city: formData.city
                            });
                            setFormData(prev => ({ ...prev, description: finalDescription }));
                            setSuccess('AI description generated successfully!');
                            descriptionGenerated = true;
                        }
                        // Format 5: { result: "..." } or nested structures
                        else if (data.result) {
                            console.log('Found description in data.result format');
                            const result = typeof data.result === 'string' ? data.result : data.result.description || data.result.text || data.result.content;
                            if (result) {
                                const finalDescription = ensureMinWords(result, 750, {
                                    name: formData.name,
                                    category: formData.category,
                                    city: formData.city
                                });
                                setFormData(prev => ({ ...prev, description: finalDescription }));
                                setSuccess('AI description generated successfully!');
                                descriptionGenerated = true;
                            }
                        }
                        // Check for error messages
                        else if (data.error) {
                            console.error('Backend returned error:', data.error);
                            setError(`Backend error: ${data.error}`);
                        }
                        else {
                            console.warn('Unexpected response format:', data);
                            console.warn('Available keys in response:', Object.keys(data));
                        }
                    }
                } else {
                    // Response not OK
                    const errorText = await response.text();
                    console.error('Response not OK. Status:', response.status, 'Text:', errorText);
                    let detailedError = '';
                    try {
                        const parsed = JSON.parse(errorText);
                        detailedError = parsed?.error || parsed?.message || '';
                    } catch (_) {
                        detailedError = errorText?.substring(0, 200) || '';
                    }
                    setError(`Server error: ${response.status} - ${response.statusText}${detailedError ? ` | ${detailedError}` : ''}`);
                }
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.log('Request timeout - using fallback');
                } else {
                    console.error('Fetch error:', fetchError);
                }
            }
        } catch (err) {
            console.error('Error in API call:', err);
        }

        // If API didn't work, always use fallback
        if (!descriptionGenerated) {
            try {
                const fallbackDescription = generateFallbackDescription(
                    formData.name,
                    formData.category,
                    formData.city,
                    820
                );
                console.log('Using fallback description:', fallbackDescription);
                setFormData(prev => ({ ...prev, description: fallbackDescription }));
                setSuccess('Description generated successfully!');
            } catch (fallbackErr) {
                console.error('Fallback generation error:', fallbackErr);
                setError('Failed to generate description. Please enter one manually.');
            }
        }

        setGenerating(false);
        
        // Clear success message after 4 seconds
        if (descriptionGenerated || !descriptionGenerated) {
            setTimeout(() => setSuccess(null), 4000);
        }
    };

    // Function to generate business image with AI
    const handleGenerateImage = async () => {
        if (!formData.name || !formData.category || !formData.city) {
            setError('Please enter Business Name, Category, and City to generate an image.');
            setTimeout(() => setError(null), 3000);
            return;
        }
        
        setGeneratingImage(true);
        setError(null);
        setSuccess(null);
        
        try {
            console.log('Generating image with:', {
                name: formData.name,
                category: formData.category,
                city: formData.city
            });

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for image generation

            try {
                const response = await fetch(`${API_BASE_URL}/generate_description.php`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        name: formData.name,
                        category: formData.category,
                        city: formData.city,
                        generate_image: true
                    }),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);
                console.log('Image generation response status:', response.status);

                if (response.ok) {
                    const responseText = await response.text();
                    console.log('Image generation response:', responseText);
                    
                    try {
                        const data = JSON.parse(responseText);
                        console.log('Parsed image data:', data);

                        if (data.success && data.image_url) {
                            // Use proxy URL if available (better for CORS), otherwise use direct URL
                            const imageUrlToUse = data.proxy_url || data.image_url;
                            setGeneratedImageUrl(imageUrlToUse);
                            console.log('Image URL set:', imageUrlToUse);
                            setSuccess('Business image generated successfully! Click "Use This Image" to set it as profile photo.');
                            setTimeout(() => setSuccess(null), 5000);
                        } else if (data.error) {
                            throw new Error(data.error);
                        } else {
                            throw new Error('Invalid response format');
                        }
                    } catch (parseError) {
                        console.error('Failed to parse image response:', parseError);
                        setError('Failed to parse image generation response. Please try again.');
                        setTimeout(() => setError(null), 5000);
                    }
                } else {
                    const errorText = await response.text();
                    console.error('Image generation error:', errorText);
                    setError(`Server error: ${response.status}. Image generation may not be available.`);
                    setTimeout(() => setError(null), 5000);
                }
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.log('Image generation timeout');
                    setError('Image generation timed out. Please try again.');
                } else {
                    console.error('Image generation fetch error:', fetchError);
                    setError('Failed to generate image. Please try again.');
                }
                setTimeout(() => setError(null), 5000);
            }
        } catch (err) {
            console.error('Error generating image:', err);
            setError('Failed to generate image. Please try again.');
            setTimeout(() => setError(null), 5000);
        } finally {
            setGeneratingImage(false);
        }
    };

    // Function to use the generated image as profile photo
    const handleUseGeneratedImage = async () => {
        if (!generatedImageUrl) return;

        setLoading(true);
        setError(null);

        try {
            console.log('Fetching generated image from:', generatedImageUrl);
            
            // Fetch the image with CORS mode
            const response = await fetch(generatedImageUrl, {
                mode: 'cors',
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            console.log('Image blob created:', blob.type, blob.size);
            
            // Validate it's an image
            if (!blob.type.startsWith('image/')) {
                throw new Error('Invalid image type: ' + blob.type);
            }
            
            // Create a File object from the blob
            const fileName = `${formData.name.replace(/\s+/g, '_')}_generated.${blob.type.split('/')[1] || 'jpg'}`;
            const file = new File([blob], fileName, { type: blob.type });
            
            console.log('File created:', file.name, file.type, file.size);
            
            setProfilePhotos([file]);
            setSuccess('Generated image set as profile photo! You can now submit the form.');
            setTimeout(() => setSuccess(null), 5000);
        } catch (err) {
            console.error('Error using generated image:', err);
            setError(`Failed to load generated image: ${err.message}. You can manually download the image and upload it.`);
            setTimeout(() => setError(null), 8000);
        } finally {
            setLoading(false);
        }
    };

    // Function to call AI API directly (if API key is configured in .env)
    const callDirectAIApi = async (apiKey, apiType, name, category, city, controller) => {
        const prompt = `Generate a comprehensive, professional business description for "${name}", a ${category} located in ${city}. The description should be approximately 800 words. Include an engaging introduction, detailed services/products, unique selling points, location benefits, and why customers should choose this business. Make it compelling, informative, SEO-friendly, and well-structured with proper paragraphs.`;

        try {
            if (apiType === 'openai') {
                const response = await fetch('https://api.openai.com/v1/chat/completions', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${apiKey}`
                    },
                    body: JSON.stringify({
                        model: 'gpt-3.5-turbo',
                        messages: [
                            { role: 'system', content: 'You are a professional business description writer. Create comprehensive, detailed, SEO-friendly business descriptions that are approximately 800 words. Use proper paragraph structure and engaging content.' },
                            { role: 'user', content: prompt }
                        ],
                        max_tokens: 1300,
                        temperature: 0.7
                    }),
                    signal: controller.signal
                });

                if (response.ok) {
                    const data = await response.json();
                    return {
                        ok: true,
                        text: async () => JSON.stringify({
                            success: true,
                            description: data.choices[0]?.message?.content?.trim() || ''
                        })
                    };
                }
            } else if (apiType === 'google') {
                const models = ['gemini-1.5-flash-latest', 'gemini-1.5-flash', 'gemini-1.5-pro-latest', 'gemini-pro'];
                let lastError = null;

                for (const model of models) {
                    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            contents: [{ parts: [{ text: prompt }] }],
                            generationConfig: { temperature: 0.7, maxOutputTokens: 1300 }
                        }),
                        signal: controller.signal
                    });

                    if (response.ok) {
                        const data = await response.json();
                        return {
                            ok: true,
                            text: async () => JSON.stringify({
                                success: true,
                                description: data.candidates[0]?.content?.parts[0]?.text?.trim() || ''
                            })
                        };
                    }

                    const errText = await response.text();
                    lastError = errText;
                    if (!/not found|not supported/i.test(errText)) break;
                }

                console.error('Google direct API failed:', lastError);
                return { ok: false, status: 500, statusText: 'Google API call failed' };
            }
        } catch (err) {
            console.error('Direct AI API error:', err);
        }

        return { ok: false, status: 500, statusText: 'AI API call failed' };
    };

    // Enhanced function to generate a professional description
    const generateFallbackDescription = (name, category, city, targetWords = 820) => {
        const sections = [
            `${name} is a trusted ${category} business in ${city}, built on a strong commitment to quality, reliability, and customer satisfaction. Our mission is simple: to deliver professional service, practical solutions, and a smooth customer experience from the first interaction to the final outcome. Over time, we have developed a reputation for understanding customer needs and responding with personalized support, transparent communication, and dependable execution.`,
            `At ${name}, we offer a complete range of ${category} services designed for modern customers in ${city}. Whether you are looking for quick assistance, long-term service support, or expert consultation, our team is trained to guide you at every step. We focus on quality standards, timely delivery, and professional behavior so that every customer feels valued and confident in choosing us. Our approach is not one-size-fits-all; we listen carefully and recommend solutions that match each requirement and budget.`,
            `What makes ${name} different is our attention to detail and customer-first mindset. We combine practical experience with updated methods to ensure better outcomes and long-term value. We work with verified processes, quality checks, and clear communication to reduce confusion and increase trust. Every service request is handled with care, and we consistently aim to exceed expectations through dependable work quality and responsive support.`,
            `Being based in ${city} gives ${name} a strong local advantage. We understand local preferences, seasonal needs, service timelines, and customer expectations in this market. This local understanding helps us provide faster response times, relevant recommendations, and consistent support. Our customers appreciate that we are approachable, accountable, and available when needed. We are proud to contribute to the local business ecosystem while maintaining high standards of professionalism and service quality.`,
            `Customers choose ${name} because we prioritize trust, quality, and long-term relationships. We believe great service is not just about completing one task, but about building confidence through every interaction. From inquiry to after-service support, our team stays focused on creating a hassle-free experience. If you are searching for a reliable ${category} in ${city}, ${name} is ready to help with professional expertise, transparent service, and a strong commitment to your satisfaction.`
        ];

        let finalText = sections.join('\n\n');

        // Add non-repetitive supporting paragraphs so generated text stays natural
        // and does not duplicate the same line many times.
        const supportingParagraphs = [
            `${name} follows a structured service workflow that begins with understanding customer requirements and ends with clear post-service follow-up. This process-driven approach helps us maintain consistency, reduce turnaround time, and improve customer confidence in every interaction.`,
            `Our team at ${name} values professionalism, punctuality, and practical problem-solving. We invest in skill development, service standards, and customer communication so clients receive dependable ${category} support in ${city} with measurable value.`,
            `We also focus on long-term customer relationships by offering responsive assistance, clear service expectations, and transparent communication at every stage. This commitment helps customers in ${city} choose ${name} as a reliable ${category} partner.`,
            `${name} regularly reviews customer feedback to refine internal processes and enhance service quality. By continuously improving how we plan, execute, and support our services, we ensure better outcomes and a smoother overall experience.`,
            `From first-time customers to repeat clients, ${name} aims to deliver dependable ${category} solutions with clarity, accountability, and strong service ethics. Our goal is to make every customer interaction simple, positive, and result-oriented.`
        ];

        let i = 0;
        while (countWords(finalText) < targetWords && i < supportingParagraphs.length) {
            finalText += `\n\n${supportingParagraphs[i]}`;
            i += 1;
        }

        return finalText.trim();
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            // support multiple file selection
            setProfilePhotos(Array.from(files || []));
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value
            }));
        }
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('Form submitted with data:', formData);
        console.log('User:', user);
        console.log('Profile photos:', profilePhotos);

        // Validate form - auto-clear invalid optional fields
        let cleanedFormData = { ...formData };
        
        console.log('=== FORM SUBMISSION DEBUG ===');
        console.log('formData before cleanup:', formData);
        
        // If email or phone are provided but invalid, clear them (they are optional)
        if (formData.email && !VALIDATION.EMAIL_REGEX.test(formData.email)) {
            console.warn('Invalid email format, clearing email field');
            cleanedFormData.email = '';
        }
        if (formData.phone && !VALIDATION.PHONE_REGEX.test(formData.phone)) {
            console.warn('Invalid phone format, clearing phone field');
            cleanedFormData.phone = '';
        }
        
        console.log('cleanedFormData:', cleanedFormData);
        
        // Only require name and description - these are critical
        // Other fields like category and city are nice-to-haves
        const minimalValidationSchema = {
            name: businessValidationSchema.name,
            description: businessValidationSchema.description
        };
        
        const validationErrors = validateForm(cleanedFormData, minimalValidationSchema);
        console.log('Validation errors (minimal check):', validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            // Build detailed error message
            const errorFields = Object.keys(validationErrors)
                .map(field => `${field}: ${validationErrors[field]}`)
                .join('; ');
            console.error('❌ Form validation failed:', validationErrors);
            console.error('Error details:', errorFields);
            setError(`⚠️ Cannot submit: ${errorFields}. Please fill in the required fields marked with red border.`);
            window.scrollTo(0, 0);
            return;
        }
        
        console.log('✓ Validation passed! Ready to submit.');
        // Use cleaned form data for submission
        const formDataToSubmit = cleanedFormData;

        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            Object.entries(formDataToSubmit).forEach(([key, value]) => {
                if (key === 'name') {
                    formDataToSend.append('businessName', value);
                }
                formDataToSend.append(key, value);
            });
            if (profilePhotos && profilePhotos.length > 0) {
                // keep compatibility by sending the first selected file as `profile_photo`
                formDataToSend.append('profile_photo', profilePhotos[0]);
                // also append all selected files as an array for backends that support it
                profilePhotos.forEach((f) => {
                    formDataToSend.append('profile_photos[]', f);
                });
            } else {
                formDataToSend.append('profile_photo', '');
            }
            // Add user ID to the form data
            if (user && user.id) {
                formDataToSend.append('userId', user.id);
            }

            console.log('Submitting business data:', formDataToSubmit);
            console.log('User ID:', user?.id);

            // Get auth token
            const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

            // Try the main API endpoint first
            let response;
            let result;

            try {
                console.log('Trying main API endpoint:', `${API_BASE_URL}/addBusiness.php`);
                response = await fetch(`${API_BASE_URL}/addBusiness.php`, {
                    method: 'POST',
                    headers: headers,
                    body: formDataToSend
                });
                console.log('Main API response status:', response.status);

                // Check if response is ok before parsing
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Main API error response:', errorText);
                    throw new Error(`Server error: ${response.status} - ${response.statusText}. ${errorText}`);
                }

                // Try to parse JSON, handle non-JSON responses
                const responseText = await response.text();
                try {
                    result = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON response:', responseText);
                    throw new Error(`Invalid server response: ${responseText.substring(0, 100)}`);
                }
                console.log('Main API response data:', result);
            } catch (apiError) {
                console.log('Main API failed, trying fallback...', apiError);
                try {
                    response = await fetch(`${API_BASE_URL}/businesses.php`, {
                        method: 'POST',
                        headers: headers,
                        body: formDataToSend
                    });
                    console.log('Fallback API response status:', response.status);

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('Fallback API error response:', errorText);
                        throw new Error(`Server error: ${response.status} - ${response.statusText}. ${errorText}`);
                    }

                    const responseText = await response.text();
                    try {
                        result = JSON.parse(responseText);
                    } catch (parseError) {
                        console.error('Failed to parse JSON response:', responseText);
                        throw new Error(`Invalid server response: ${responseText.substring(0, 100)}`);
                    }
                    console.log('Fallback API response data:', result);
                } catch (fallbackError) {
                    console.error('Both API endpoints failed:', fallbackError);
                    throw new Error(fallbackError.message || 'Unable to connect to server. Please check your internet connection.');
                }
            }

            if (!result || !result.success) {
                throw new Error(result?.message || result?.error || 'Server returned an error. Please check all required fields are filled correctly.');
            }

            // Show success message
            // Show success message
            setSuccess('Business added successfully! Redirecting...');
            setTimeout(() => {
                navigate(ROUTES.DASHBOARD);
            }, 2000);
        } catch (err) {
            setError(err.message || MESSAGES.ERROR.GENERAL);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid>
            <Row>
                {/* Main Content Only - Sidebar removed */}
                <Col md={{ span: 8, offset: 2 }} className="p-4">
                    <h2 className="mb-4">Add New Business</h2>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" className="mt-3">
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="mt-3 add-business-grid0">
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Business Name</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaUser />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!errors.name}
                                            placeholder="Business Name"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaEnvelope />
                                        </span>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            isInvalid={!!errors.email}
                                            placeholder="Business Email"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Address</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaMapMarkerAlt />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            isInvalid={!!errors.address}
                                            placeholder="Address"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.address}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>State</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaFlag />
                                        </span>
                                        {/* <Form.Control
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            isInvalid={!!errors.state}
                                            placeholder="State"
                                        /> */}
                                        {/* Autocomplete for state */}
                                        <Typeahead
                                            id="state-typeahead"
                                            onChange={selected => {
                                                const stateValue = selected && selected.length > 0 ? selected[0] : '';
                                                setFormData(prev => ({ ...prev, state: stateValue }));
                                            }}
                                            options={indianStates}
                                            placeholder="Start typing state..."
                                            selected={formData.state ? [formData.state] : []}
                                            className="mb-2"
                                        />
                                    </div>


                                    <Form.Control.Feedback type="invalid">
                                        {errors.state}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaCity />
                                        </span>
                                        {/* <Form.Control
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                                            isInvalid={!!errors.city}
                                            placeholder="City"
                                        /> */}
                                        {/* Autocomplete for city */}
                                        <Typeahead
                                            id="city-typeahead"
                                            onChange={selected => {
                                                const cityValue = selected && selected.length > 0 ? selected[0] : '';
                                                console.log('City selected:', cityValue);
                                                setFormData(prev => ({ ...prev, city: cityValue }));
                                            }}
                                            options={majorIndianCities}
                                            placeholder="Start typing city..."
                                            selected={formData.city ? [formData.city] : []}
                                            className="mb-2"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.city}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Zip Code</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaHashtag />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="zip_code"
                                            value={formData.zip_code}
                                            onChange={handleChange}
                                            isInvalid={!!errors.zip_code}
                                            placeholder="Zip Code"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.zip_code}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaPhone />
                                        </span>
                                        <Form.Control
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            isInvalid={!!errors.phone}
                                            placeholder="Phone"
                                        />

                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phone}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Category</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaListAlt />
                                        </span>
                                        {/* <Form.Control
                                            type="text"
                                            name="category"
                                            value={formData.category}
                                            onChange={e => setFormData({ ...formData, category: e.target.value })}
                                            isInvalid={!!errors.category}
                                            placeholder="Category"
                                        /> */}
                                        {/* Autocomplete for category */}
                                        <Typeahead
                                            id="category-typeahead"
                                            onChange={selected => {
                                                const categoryValue = selected && selected.length > 0 ? selected[0] : '';
                                                console.log('Category selected:', categoryValue);
                                                setFormData(prev => ({ ...prev, category: categoryValue }));
                                            }}
                                            options={businessCategories}
                                            placeholder="Start typing category..."
                                            selected={formData.category ? [formData.category] : []}
                                            className="mb-2"
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.category}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                {/* AI Description Button */}
                                <div className="mb-2">
                                    <Button
                                        type="button"
                                        variant="outline-info"
                                        onClick={handleGenerateDescription}
                                        disabled={generating}
                                        className="mb-2"
                                        style={{ 
                                            cursor: generating ? 'not-allowed' : 'pointer',
                                            pointerEvents: generating ? 'none' : 'auto'
                                        }}
                                    >
                                        {generating ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Generating...
                                            </>
                                        ) : (
                                            '🤖 Generate Description with AI'
                                        )}
                                    </Button>
                                    {(!formData.name || !formData.category || !formData.city) && !generating && (
                                        <small className="text-muted d-block">
                                            <i className="fa fa-info-circle me-1"></i>
                                            Fill in Business Name, Category, and City for best results
                                        </small>
                                    )}
                                </div>
                                {/* Description Field */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaAlignLeft />
                                        </span>
                                        <Form.Control
                                            as="textarea"
                                            rows={8}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            isInvalid={!!errors.description}
                                            placeholder="Description (approximately 800 words)"
                                            style={{ minHeight: '200px' }}
                                        />
                                    </div>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Profile Photo</Form.Label>
                                    
                                    {/* Generate Image Button */}
                                    <div className="mb-2">
                                        <Button
                                            type="button"
                                            variant="outline-success"
                                            onClick={handleGenerateImage}
                                            disabled={generatingImage || !formData.name || !formData.category || !formData.city}
                                            className="me-2"
                                        >
                                            {generatingImage ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Generating Image...
                                                </>
                                            ) : (
                                                '🎨 Generate Business Image with AI'
                                            )}
                                        </Button>
                                        {(!formData.name || !formData.category || !formData.city) && !generatingImage && (
                                            <small className="text-muted d-block mt-1">
                                                Fill in Business Name, Category, and City to enable AI image generation
                                            </small>
                                        )}
                                    </div>

                                    {/* Generated Image Preview */}
                                    {generatedImageUrl && (
                                        <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <strong>Generated Image Preview:</strong>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="success"
                                                        size="sm"
                                                        onClick={handleUseGeneratedImage}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Loading...
                                                            </>
                                                        ) : (
                                                            '✓ Use This Image'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => window.open(generatedImageUrl, '_blank')}
                                                    >
                                                        🔗 Open
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <img 
                                                    src={generatedImageUrl} 
                                                    alt="Generated business image" 
                                                    style={{ 
                                                        maxWidth: '100%', 
                                                        maxHeight: '300px', 
                                                        borderRadius: '8px',
                                                        border: '2px solid #dee2e6',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => window.open(generatedImageUrl, '_blank')}
                                                    onError={(e) => {
                                                        console.error('Image load error');
                                                        setError('Failed to load generated image. The URL may have expired. Please generate a new image.');
                                                    }}
                                                />
                                            </div>
                                            <small className="text-muted d-block mt-2 text-center">
                                                💡 Tip: If "Use This Image" doesn't work, right-click the image → "Save As", then upload it manually.
                                            </small>
                                        </div>
                                    )}

                                    {/* File Upload */}
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaFileImage />
                                        </span>
                                        <Form.Control
                                            type="file"
                                            name="profile_photos"
                                            accept="image/*"
                                            multiple
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {profilePhotos && profilePhotos.length > 0 && (
                                        <div className="mt-1">
                                            <small className="text-muted d-block">Selected ({profilePhotos.length}):</small>
                                            <div className="d-flex gap-2 mt-1" style={{flexWrap: 'wrap'}}>
                                                {profilePhotos.map((f, idx) => (
                                                    <div key={idx} style={{textAlign: 'center'}}>
                                                        <img
                                                            src={URL.createObjectURL(f)}
                                                            alt={f.name}
                                                            style={{width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e6e6e6'}}
                                                        />
                                                        <div style={{maxWidth: 80}}>
                                                            <small className="text-muted">{f.name}</small>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Adding...' : 'Add Business'}
                            </Button>

                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default AddBusiness;







